import React, { FC } from 'react';
import styled from '@emotion/styled';
import { Box, Button } from '~Styles';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { useButton } from '~Hooks';

const Row = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  background-color: var(--colorBlueGray1100);
  padding: 16px;
  min-height: 64px;
  border-bottom: 1px solid var(--colorBlueGray1200);

  &:first-of-type {
    border-radius: 8px 8px 0 0;
  }

  &:last-of-type {
    border-radius: 0 0 8px 8px;
    border-bottom: none;
  }

  &:only-of-type {
    border-radius: 8px;
  }
`;

const IndicatorBall = styled(Box)`
  border-radius: 50%;
  width: 12px;
  height: 12px;
`;

const StockIndicator = styled(Box)`
  position: absolute;
  left: 60%;
  transform: translateX(-50%);
`;

/**
 * Formats product names as "Used in: X, Y & Z more"
 * Shows up to 2 names, then summarizes the rest.
 */
const formatUsedIn = (productNames: string[], i18n: (val: any) => string): string => {
  if (productNames.length === 0) return i18n('Inventory.NotUsedInAnyProducts');
  if (productNames.length === 1) return `${i18n('Inventory.UsedIn')}: ${productNames[0]}`;
  if (productNames.length === 2) return `${i18n('Inventory.UsedIn')}: ${productNames[0]} & ${productNames[1]}`;

  const remaining = productNames.length - 2;
  return `${i18n('Inventory.UsedIn')}: ${productNames[0]}, ${productNames[1]} & ${remaining} ${i18n('Inventory.AndMore')}`;
};

interface Props {
  name: string;
  productNames: string[];
  isOutOfStock: boolean;
  onToggleStock: () => void;
  i18n: (val: any) => string;
}

const VariationRow: FC<Props> = ({ name, productNames, isOutOfStock, onToggleStock, i18n }) => {
  const stockButton = useButton({
    onClick: onToggleStock,
  });

  return (
    <Row>
      <Box display="flex" flexDirection="column">
        <Heading level={5} color="var(--colorWhite)" bottom={0}>
          {name}
        </Heading>
        <Paragraph level={5} color="var(--colorBlueGray200)">
          {formatUsedIn(productNames, i18n)}
        </Paragraph>
      </Box>

      <StockIndicator display="inline-flex" alignItems="center">
        <IndicatorBall
          backgroundColor={isOutOfStock ? 'colorStatusRed' : 'colorStatusGreen'}
          marginRight="xs"
        />
        <Paragraph level={5} color="var(--colorBlueGray200)">
          {isOutOfStock ? i18n('Inventory.OutOfStock') : i18n('Inventory.InStock')}
        </Paragraph>
      </StockIndicator>

      {isOutOfStock ? (
        <Button {...stockButton} padding="small">
          {i18n('Inventory.RemoveOutOfStock')}
        </Button>
      ) : (
        <Button {...stockButton} padding="small">
          {i18n('Inventory.SetOutOfStock')}
        </Button>
      )}
    </Row>
  );
};

export default VariationRow;
